import {Component, computed, inject, OnDestroy, signal} from '@angular/core';
import {RouterModule, RouterOutlet} from '@angular/router';
import {Subject, take, takeUntil} from 'rxjs';
import {TranslocoDirective, TranslocoService} from "@jsverse/transloco";
import {FormsModule} from "@angular/forms";
import {
  ActivityIcon,
  CircleQuestionMarkIcon,
  HandshakeIcon,
  HeartIcon,
  HouseIcon,
  InfoIcon,
  LayoutDashboardIcon,
  LogInIcon,
  LogOutIcon,
  LucideAngularModule,
  MenuIcon,
  MessageCircleIcon,
  MicIcon,
  SearchIcon,
  UserIcon,
  UserStarIcon,
  XIcon
} from 'lucide-angular';
import {User, UserRole, UserState} from '@domain/user/user';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {AuthService} from '@core/auth/auth.service';
import {
  UserMembershipSelectorComponent
} from '@domain/user/user-membership-selector/user-membership-selector.component';
import {UserMembershipSelectorService} from '@domain/user/user-membership-selector/user-membership-selector.service';

@Component({
  selector: 'app-menu',
  imports: [
    RouterModule,
    RouterOutlet,
    FormsModule,
    TranslocoDirective,
    LucideAngularModule,
    UserMembershipSelectorComponent
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnDestroy {

  public user: User | null = null;
  public readonly routes = {
    dashboard: RouteGenerator.abs(UI_ROUTES.account.dashboard),
    home: RouteGenerator.abs(UI_ROUTES.public.dashboard),
    login: RouteGenerator.abs(UI_ROUTES.auth.login),
    register: RouteGenerator.abs(UI_ROUTES.auth.register),
    hosts: RouteGenerator.abs(UI_ROUTES.account.host.dashboard),
    supportUs: RouteGenerator.abs(UI_ROUTES.public.supportUs),
    publishGuest: RouteGenerator.abs(UI_ROUTES.public.activity.publish),
    publish: RouteGenerator.abs(UI_ROUTES.account.activity.create),
    about: RouteGenerator.abs(UI_ROUTES.public.about),
    faq: RouteGenerator.abs(UI_ROUTES.public.faq),
    terms: RouteGenerator.abs(UI_ROUTES.public.terms.home),
    partners: RouteGenerator.abs(UI_ROUTES.public.partners),
    contact: RouteGenerator.abs(UI_ROUTES.public.contact),
    validateEmail: RouteGenerator.abs(UI_ROUTES.auth.validate),
    account: RouteGenerator.abs(UI_ROUTES.account.user.settings),
    moderate: RouteGenerator.abs(UI_ROUTES.admin.dashboard),
  };

  public readonly icons = {
    dashboard: LayoutDashboardIcon,
    account: UserIcon,
    admin: UserStarIcon,
    activity: ActivityIcon,
    host: HouseIcon,
    publish: MicIcon,
    search: SearchIcon,
    about: InfoIcon,
    partners: HandshakeIcon,
    contact: MessageCircleIcon,
    faq: CircleQuestionMarkIcon,
    supportUs: HeartIcon,
    login: LogInIcon,
    logout: LogOutIcon,
    menu: MenuIcon,
    close: XIcon
  };

  public readonly availableLangs: string[];
  public readonly userStates = UserState;
  public readonly userRoles = UserRole;
  public activeLang: string;
  private readonly destroy$ = new Subject<void>();

  public readonly isMenuOpen = signal(false);

  private readonly userMembershipSelectorService = inject(UserMembershipSelectorService);
  public readonly memberships = this.userMembershipSelectorService.memberships;
  public readonly currentMembership = this.userMembershipSelectorService.currentMembership;

  public readonly hostPreviewRoute = computed(() => {
    const hostId = this.currentMembership()?.host?.id;
    return hostId ? RouteGenerator.abs(UI_ROUTES.account.host.home, {hostId: hostId}) : null;
  });

  public constructor(
      private readonly authService: AuthService,
      private readonly translocoService: TranslocoService
  ) {
    this.authService.getAuthenticatedUserUpdates().pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.user = user;
    });
    this.translocoService.langChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(_ => {
      this.activeLang = this.translocoService.getActiveLang();
    });
    this.availableLangs = this.translocoService.getAvailableLangs() as string[];
  }

  public toggleMenu() {
    this.isMenuOpen.update(open => !open);
  }

  public hideMenu() {
    this.isMenuOpen.set(false);
  }

  public logout(): void {
    this.authService.logout().pipe(
      take(1)
    ).subscribe(_ => {
      this.hideMenu();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public switchLanguage(lang: string) {
    this.authService.setLanguage(lang, true).pipe(
      take(1)
    ).subscribe(_ => {
      this.activeLang = lang;
    });
  }


}
